import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import { APIScope, MemberInterface, PulsePersona } from "@swivl-mono/types";
import { Avatar, Button, ButtonColor, LayoutHeader } from "@swivl-mono/ui";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { ShowIf } from "../../../../Components/ShowIf/ShowIf";
import { isPulseEnabled } from "../../../../Helpers/FeatureFlags/isPulseEnabled";
import { useHasPermission } from "../../../../Helpers/Permissions/useHasPermission";
import LocationsModel from "../../../../Models/Locations/Locations.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import MembersModel from "../../../../Models/Member/Members.Model";
import { AlertsModel } from "../../../../Models/Notifications/Alerts.Model";
import { PulsePersonaSelect } from "./PulsePersonaSelect";
import { MemberOrInvitePermissionsRole } from "./Role/MemberOrInvitePermissionsRole.View";

interface Props {
  member: MemberInterface;
}

export function MemberPermissionsView(props: Props) {
  const navigate = useNavigate();
  const canManageMembers = useHasPermission(APIScope.MEMBERS_SET);
  const { member } = props;

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
  }, []);

  const handlePersonaChange = async (persona: PulsePersona | null): Promise<void> => {
    try {
      const saved = await API.Members.setPersona(Member.orgId!, member.id, persona);
      void MembersModel.updateMember({ id: saved.id, persona: saved.persona ?? null }, true);
      AlertsModel.successToast("Pulse persona saved.");
    } catch (err) {
      warn("Saving the member's Pulse persona failed", err);
      AlertsModel.errorToast("The persona couldn't be saved.");
    }
  };

  return (
    <div className="flex flex-col px-6 pb-6">
      <LayoutHeader noPadding={true}>
        <Avatar className="mr-2" src={member.account.avatarURL} />
        <h2 className="font-semibold">
          {member.account.firstName} {member.account.lastName}
        </h2>

        <ShowIf scope={APIScope.MEMBERS_SET}>
          <Button
            className="remove mr-2 ml-auto"
            color={ButtonColor.WHITE}
            onClickAsync={async () => {
              if (window.confirm("Are you sure you want to remove this member?")) {
                await MembersModel.deleteMember(member.id);
                AlertsModel.successToast("Member removed successfully.");
                navigate("/settings/members");
              }
            }}
          >
            Remove Member
          </Button>
        </ShowIf>
        <ShowIf scope={APIScope.MEMBERS_SET}>
          <Button
            className="ml-auto"
            color={ButtonColor.WHITE}
            onClickAsync={async () => {
              await MembersModel.addNewRoleToMember(member.id);
            }}
          >
            + Add Role
          </Button>
        </ShowIf>
      </LayoutHeader>
      <div className="flex flex-row items-center pb-3">
        <p className="font-semibold">Email:</p>
        <p className="pl-2">{member.account.email}</p>
      </div>
      <div className="flex flex-row items-center pb-3">
        <p className="">Joined: {DateTime.fromISO(member.createdAt as any).toLocaleString()}</p>
      </div>
      <div className="flex flex-row items-center pb-3">
        <p>Last Active: {member.lastActive ? DateTime.fromISO(member.lastActive as any).toLocaleString() : "Unknown"}</p>
      </div>
      {isPulseEnabled() && (
        <div className="w-64 pb-4">
          <PulsePersonaSelect persona={member.persona ?? null} disabled={!canManageMembers} onChange={handlePersonaChange} />
        </div>
      )}

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-grey-800">Permissions</h3>
      </div>
      {!member.permissions?.length ? (
        <div className="flex flex-row items-center pb-3">
          <p className="font-semibold">This member has no assigned roles.</p>
        </div>
      ) : (
        member.permissions.map((permission) => <MemberOrInvitePermissionsRole member={member} memberPermission={permission} key={permission.id} />)
      )}
    </div>
  );
}
